import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers';
import { useAuth } from '@clerk/clerk-react';
import { shouldClearLivePlayStateOverlayAfterCueRefetch } from '@/helpers/merge-live-play-state.helper';
import {
  GET_LIVE_CONTEXT_STATE_QUERY_KEY,
  type LiveContextStateDto,
} from '@/state/queries/player/use-get-live-context-state';
import {
  entityRoomKeyFromEntityRef,
  replaceCueStateFromGet,
  usePerformControlWsStore,
} from '@/state/player/perform-control-ws.store';
import {
  latchNextCueRecalculatingForJump,
  markNextCueRecalculatingJumpFailed,
} from '@/state/player/next-cue-recalculating.store';
import { clearLivePlayStateOverlay, useLivePlayStateWsStore } from '@/state/player/live-play-state-ws.store';
import {
  resolveStartSlideIndexForTake,
  seedLiveSlideIndexForTake,
} from '@/helpers/resolve-start-slide-index-for-take.helper';
import { useLiveSlideChromeSuppressStore } from '@/state/stores/use-live-slide-chrome-suppress.store';
import { useFlowControlSimulationCueStore } from '@/state/stores/use-flow-control-simulation-cue-store';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { resolveFlowEntityIdForPlayers } from '@/helpers/object.id.helper';
import {
  GET_FLOW_MEDIA_AGENT_QUERY_KEY,
  type FlowMediaAgentState,
} from '@/state/queries/playout/use-get-flow-media-agent';
import { isAgentDeckItemId } from '@/helpers/is-agent-deck-item.helper';
import { collectFlowMediaById } from '@/helpers/simulation-cue-playability.helper';
import { resolveMediaEngineDeckSwitchOnTake } from '@/helpers/preview/resolve-operator-current-agent-shield.helper';
import type { FlowCueStateV1, LiveCueSheetV1 } from '@/types/player/perform-control-v1.types';
import { liveCueSheetToSimulationRows } from '@/helpers/live-cue-sheet-to-simulation-rows.helper';
import { resolveExpectedCueWindowAfterJump } from '@/helpers/resolve-expected-cue-window-after-jump.helper';
import { shouldSkipCueCommandSuccessRefetch as shouldSkipLiveContextGetAfterCueCommand } from '@/helpers/should-skip-cue-command-success-refetch.helper';
import { isCueItemNotOnLiveSheetError } from '@/helpers/is-cue-item-not-on-live-sheet-error.helper';
import { useNotifications } from '@/hooks/use-notifications';
import { useTranslations } from '@/i18n/use-translations';

function isPresentationFileOnCachedFlow(itemId: string): boolean {
  if (!itemId.startsWith('media:')) {
    return false;
  }
  const flow = useFlowStore.getState().flowPlan ?? useFlowStore.getState().flow;
  if (!flow) {
    return false;
  }
  const media = collectFlowMediaById(flow).get(itemId.slice('media:'.length));
  const asset = media?.assets?.find((entry) => entry.isApproved) ?? media?.assets?.[0];
  return asset?.fileType === 'presentation';
}

/** Operator switch off, or the flow is not entitled: decks are slide images, not the media engine. */
function isPresentationComputerOff(queryClient: QueryClient): boolean {
  const flow = useFlowStore.getState().flowPlan ?? useFlowStore.getState().flow;
  const ids = flow ? [flow.id, resolveFlowEntityIdForPlayers(flow)] : [];
  for (const id of ids) {
    const state = queryClient.getQueryData<FlowMediaAgentState>([GET_FLOW_MEDIA_AGENT_QUERY_KEY, id]);
    if (state && (!state.entitled || !state.enabled)) {
      return true;
    }
  }
  return false;
}

/** Live cue sheet first — simulation rows are not updated while on air. */
function isLiveOrSimulationAgentDeck(itemId: string | undefined, entityRef: string, queryClient: QueryClient): boolean {
  if (isPresentationComputerOff(queryClient)) {
    return false;
  }
  const roomKey = entityRoomKeyFromEntityRef(entityRef);
  const wsSheet = roomKey ? usePerformControlWsStore.getState().cueSheetByRoom[roomKey] : undefined;
  const getState = queryClient.getQueryData<LiveContextStateDto>([GET_LIVE_CONTEXT_STATE_QUERY_KEY, entityRef]);
  const cueSheet = wsSheet ?? getState?.cueSheet;
  const liveRows = cueSheet ? liveCueSheetToSimulationRows(cueSheet) : [];
  return isAgentDeckItemId(itemId, {
    cueSheet,
    rows: liveRows.length > 0 ? liveRows : useFlowControlSimulationCueStore.getState().rows,
    isPresentationFile: isPresentationFileOnCachedFlow,
  });
}

function readActiveAgentDeckMediaId(entityRef: string, queryClient: QueryClient): string | undefined {
  const roomKey = entityRoomKeyFromEntityRef(entityRef);
  const wsDeck = roomKey
    ? useLivePlayStateWsStore.getState().playStateByRoom[roomKey]?.activeAgentDeckMediaId
    : undefined;
  if (wsDeck) {
    return wsDeck;
  }
  return queryClient.getQueryData<LiveContextStateDto>([GET_LIVE_CONTEXT_STATE_QUERY_KEY, entityRef])?.playState
    ?.activeAgentDeckMediaId;
}

export type CueCommandRequest =
  | { action: 'playSegment'; entityRef: string; anchor: 'flow_start' | 'flow_end' | 'slot'; slotId?: string }
  | { action: 'stopSegment'; entityRef: string; anchor: 'flow_start' | 'flow_end' | 'slot'; slotId?: string }
  | {
      action: 'playMedia';
      entityRef: string;
      itemId: string;
      cueItemId?: string;
      scope?: string;
      slotId?: string;
      cuePlayMode?: 'jump' | 'insert';
      /** 1-based slide from item/Next preview — applied when the take goes on air. */
      startSlideIndex?: number;
    }
  | {
      action: 'stopMedia';
      entityRef: string;
      itemId: string;
      cueItemId?: string;
      scope?: string;
      slotId?: string;
    }
  | {
      action: 'nextMedia';
      entityRef: string;
      cueItemId?: string;
      startSlideIndex?: number;
      /** Item being taken (pre-advance Next). Used for FE seed only — must not re-read post-advance Next. */
      itemId?: string;
    }
  | { action: 'prevMedia'; entityRef: string }
  | { action: 'nextSlide'; entityRef: string }
  | { action: 'prevSlide'; entityRef: string }
  | { action: 'selectTrack'; entityRef: string; itemId: string };

export interface UseCueCommandMutationOptions {
  onError?: (error: Error) => void;
}

type CueCommandMutationContext = {
  preCommandSeq: number;
  roomKey: string | null;
  /** nextMedia leaving play-once insert — always refetch so Next is not pinned on resume PDF. */
  leftInsertOverride?: boolean;
};

/**
 * Cue commands return after on-air commit; look-ahead preload continues on the server.
 * Mid-command `live-context-invalidated` refetches land before the on-air commit; cancel those first.
 *
 * After the post-commit GET lands:
 * - Replace the perform-control cue overlay (pointer magnitude is not recency — backward jumps).
 * - Drop the transitional play-state overlay only when REST already matches the WS preview
 *   signature, so a fresher `_preview:*` / look-ahead push is not discarded for a lagging GET.
 *
 * Skip the success GET when perform-control WS seq already advanced past the pre-command seq
 * (WS already delivered the on_air milestone). Always refetch jump/segment, prevMedia, and errors.
 */
async function refetchLiveContextAfterCueCommand(queryClient: QueryClient, entityRef: string): Promise<void> {
  const queryKey = [GET_LIVE_CONTEXT_STATE_QUERY_KEY, entityRef];
  await queryClient.cancelQueries({ queryKey });
  await queryClient.refetchQueries({ queryKey });
  const roomKey = entityRoomKeyFromEntityRef(entityRef);
  if (!roomKey) {
    return;
  }

  const liveContext = queryClient.getQueryData<LiveContextStateDto>(queryKey);
  if (liveContext?.cueState) {
    replaceCueStateFromGet(roomKey, liveContext.cueState);
  }

  const wsEntry = useLivePlayStateWsStore.getState().playStateByRoom[roomKey];
  if (shouldClearLivePlayStateOverlayAfterCueRefetch(liveContext, wsEntry)) {
    clearLivePlayStateOverlay(roomKey);
  }
}

function shouldSkipCueCommandSuccessRefetch(
  context: CueCommandMutationContext | undefined,
  variables?: CueCommandRequest,
): boolean {
  if (!context?.roomKey) {
    return false;
  }
  const currentSeq = usePerformControlWsStore.getState().lastSeqByRoom[context.roomKey] ?? 0;
  return shouldSkipLiveContextGetAfterCueCommand({
    action: variables?.action,
    cuePlayMode: variables?.action === 'playMedia' ? variables.cuePlayMode : undefined,
    seqAdvanced: currentSeq > context.preCommandSeq,
  });
}

/** GET∪WS merge prefers the higher pointer — stamp WS cueState into GET so go-back Next is not stuck. */
function seedLiveContextCueStateFromWs(queryClient: QueryClient, entityRef: string, roomKey: string): void {
  const wsCue = usePerformControlWsStore.getState().cueStateByRoom[roomKey];
  if (!wsCue) {
    return;
  }
  queryClient.setQueryData<LiveContextStateDto>([GET_LIVE_CONTEXT_STATE_QUERY_KEY, entityRef], (current) =>
    current ? { ...current, cueState: wsCue } : current,
  );
}

function isCueJumpThatRecalculatesNext(body: CueCommandRequest): body is CueCommandRequest & {
  action: 'playMedia' | 'playSegment';
} {
  if (body.action === 'playSegment') {
    return true;
  }
  return body.action === 'playMedia' && body.cuePlayMode === 'jump';
}

function readCueStateSnapshotForJump(
  queryClient: QueryClient,
  entityRef: string,
  roomKey: string | null,
): FlowCueStateV1 | undefined {
  if (roomKey) {
    const wsCue = usePerformControlWsStore.getState().cueStateByRoom[roomKey];
    if (wsCue) {
      return wsCue;
    }
  }
  return queryClient.getQueryData<LiveContextStateDto>([GET_LIVE_CONTEXT_STATE_QUERY_KEY, entityRef])?.cueState;
}

function readCueSheetSnapshotForJump(
  queryClient: QueryClient,
  entityRef: string,
  roomKey: string | null,
): LiveCueSheetV1 | undefined {
  if (roomKey) {
    const wsSheet = usePerformControlWsStore.getState().cueSheetByRoom[roomKey];
    if (wsSheet) {
      return wsSheet;
    }
  }
  return queryClient.getQueryData<LiveContextStateDto>([GET_LIVE_CONTEXT_STATE_QUERY_KEY, entityRef])?.cueSheet;
}

export const useCueCommandMutation = (options?: UseCueCommandMutationOptions) => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const notification = useNotifications();
  const t = useTranslations();

  return useMutation({
    mutationFn: async (body: CueCommandRequest) => {
      const token = await getToken();
      return cueCommand(body, token ?? '');
    },
    onMutate: (variables): CueCommandMutationContext => {
      const roomKey = entityRoomKeyFromEntityRef(variables.entityRef);
      const preCommandSeq = roomKey ? (usePerformControlWsStore.getState().lastSeqByRoom[roomKey] ?? 0) : 0;
      const cueSnapshot = readCueStateSnapshotForJump(queryClient, variables.entityRef, roomKey);
      const leftInsertOverride = variables.action === 'nextMedia' && cueSnapshot?.cueOverrideActive === true;
      if (isCueJumpThatRecalculatesNext(variables)) {
        const cueSheet = readCueSheetSnapshotForJump(queryClient, variables.entityRef, roomKey);
        const jumpWindow = resolveExpectedCueWindowAfterJump({
          rows: cueSheet ? liveCueSheetToSimulationRows(cueSheet) : [],
          targetItemId: variables.action === 'playMedia' ? variables.itemId : undefined,
          playlistContext:
            variables.action === 'playMedia'
              ? { scope: variables.scope, slotId: variables.slotId, cueItemId: variables.cueItemId }
              : undefined,
          playSegment:
            variables.action === 'playSegment' ? { anchor: variables.anchor, slotId: variables.slotId } : undefined,
        });
        latchNextCueRecalculatingForJump({
          entityRef: variables.entityRef,
          cueState: cueSnapshot,
          ...jumpWindow,
        });
      }
      // Hide Current y/x immediately — do not wait for presentationTransitionInFlight WS.
      if (variables.action === 'playMedia' || variables.action === 'nextMedia' || variables.action === 'prevMedia') {
        const fromItemId = cueSnapshot?.currentStep?.itemId;
        const toItemId =
          variables.action === 'playMedia'
            ? variables.itemId
            : variables.action === 'nextMedia'
              ? (variables.itemId ?? cueSnapshot?.nextItemId)
              : undefined;
        useLiveSlideChromeSuppressStore.getState().suppressForCueAdvance(
          variables.entityRef,
          fromItemId,
          toItemId,
          resolveMediaEngineDeckSwitchOnTake({
            fromIsAgentDeck: isLiveOrSimulationAgentDeck(fromItemId, variables.entityRef, queryClient),
            toIsAgentDeck: isLiveOrSimulationAgentDeck(toItemId, variables.entityRef, queryClient),
            toItemId,
            activeAgentDeckMediaId: readActiveAgentDeckMediaId(variables.entityRef, queryClient),
          }),
        );
      }
      // Paint Current slide chrome from cue-row / operator pick before cue rebuild finishes.
      if (variables.action === 'playMedia') {
        const startSlideIndex =
          variables.startSlideIndex ??
          resolveStartSlideIndexForTake(variables.itemId, {
            playlistContext: {
              scope: variables.scope,
              slotId: variables.slotId,
            },
          });
        seedLiveSlideIndexForTake(variables.itemId, startSlideIndex);
      } else if (variables.action === 'nextMedia' && variables.startSlideIndex != null) {
        // Prefer the itemId captured at click time. Re-reading cueState.nextItemId races with WS
        // that already advanced Next to the *following* item (C), stamping A's/B's index onto C.
        seedLiveSlideIndexForTake(variables.itemId, variables.startSlideIndex);
      }
      return { preCommandSeq, roomKey, leftInsertOverride };
    },
    onSuccess: async (_data, variables, context) => {
      if (variables.action === 'prevMedia' && context?.roomKey) {
        seedLiveContextCueStateFromWs(queryClient, variables.entityRef, context.roomKey);
      }
      // Insert→resume / jump / go-back: replace cue overlay — skipped GET leaves Next on the old item.
      if (context?.leftInsertOverride || !shouldSkipCueCommandSuccessRefetch(context, variables)) {
        await refetchLiveContextAfterCueCommand(queryClient, variables.entityRef);
      }
    },
    onError: async (error, variables) => {
      useLiveSlideChromeSuppressStore.getState().clear(variables.entityRef);
      if (isCueJumpThatRecalculatesNext(variables)) {
        markNextCueRecalculatingJumpFailed(variables.entityRef);
      }
      if (isCueItemNotOnLiveSheetError(error)) {
        notification.notifyError(t('flows.detail.control.cueCommand.notOnLiveSheet'));
      }
      await refetchLiveContextAfterCueCommand(queryClient, variables.entityRef);
      options?.onError?.(error instanceof Error ? error : new Error(String(error)));
    },
  });
};

/** Omit null/undefined optional fields — API Zod schemas reject explicit null. */
const sanitizeCueCommandBody = (body: CueCommandRequest): CueCommandRequest => {
  const entries = Object.entries(body).filter(([, value]) => value != null);
  // `itemId` on nextMedia is FE-only (seed the take target); strip before POST.
  if (body.action === 'nextMedia') {
    return Object.fromEntries(entries.filter(([key]) => key !== 'itemId')) as CueCommandRequest;
  }
  return Object.fromEntries(entries) as CueCommandRequest;
};

const cueCommand = async (body: CueCommandRequest, token: string): Promise<{ success: boolean }> => {
  const url = `${import.meta.env.VITE_API_URL}/player/cue-command`;
  return sendJSONRequest({ url, method: METHOD.POST, body: sanitizeCueCommandBody(body), token });
};
